import { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Typography, IconButton, Stack, TableSortLabel, Tooltip, Chip } from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AccountCircle from '@mui/icons-material/AccountCircle';
import * as d3 from 'd3';
import { convertNumberIntoCurrency } from '../../../../components/Utilities/HelperMethods';
import { Link, useLocation } from 'react-router-dom';

const rowHeight = 52;
const indent = 4;
const lineIndent = 4;

// build data hierarchy with d3
const buildHierarchy = (data, expandedMap, sortConfig) => {
  const root = d3.hierarchy(data);

  root.each((d) => {
    d.expanded = expandedMap[d.data.name] ?? false;
  });

  if (sortConfig.key) {
    root.sort((a, b) => {
      const aVal = Number(a.data[sortConfig.key] ?? 0);
      const bVal = Number(b.data[sortConfig.key] ?? 0);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  return root;
};

// flatten the tree
const flattenVisibleNodes = (root, expandedMap) => {
  const out = [];

  const walk = (node, depth = 0, visible = true) => {
    const isExpanded = expandedMap[node.data.name] ?? false;
    const isVisible = visible;

    out.push({ ...node, depth, isVisible });

    if (isExpanded && node.children) {
      node.children.forEach((child) => walk(child, depth + 1, isVisible));
    }
  };

  walk(root);
  return out;
};

const PendingReportsTree = ({ data, expandAll }) => {
  const location = useLocation();
  const [expandedMap, setExpandedMap] = useState({ [data.name]: true });

  const [columns, setColumns] = useState([
    { key: 'pendingCount', label: 'Pending Count', width: 150 },
    { key: 'pendingPremium', label: 'Pending Premium', width: 160 },
    { key: 'issuedCount', label: 'Issued Count', width: 130 },
    { key: 'issuedPremium', label: 'Issued Premium', width: 150 },
  ]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  });

  const handleSort = (colKey) => {
    setSortConfig((prev) => {
      if (prev.key === colKey) {
        return {
          key: colKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key: colKey, direction: 'asc' };
    });
  };

  const containerRef = useRef(null);
  const dragColumn = useRef(null);

  const treeData = useMemo(
    () => buildHierarchy(data, expandedMap, sortConfig),
    [data, expandedMap, sortConfig]
  );

  const visibleNodes = useMemo(
    () => flattenVisibleNodes(treeData, expandedMap),
    [treeData, expandedMap]
  );

  const toggleNode = (node) => {
    setExpandedMap((prev) => ({
      ...prev,
      [node.data.name]: !prev[node.data.name],
    }));
  };

  const handleDragStart = (index) => {
    dragColumn.current = index;
  };

  const handleDrop = (dropIndex) => {
    const dragIndex = dragColumn.current;
    if (dragIndex === null || dragIndex === dropIndex) return;

    const updated = [...columns];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    setColumns(updated);
    dragColumn.current = null;
  };

  useEffect(() => {
    const newMap = {};

    const walk = (node) => {
      const isRoot = node.depth === 0;
      newMap[node.data.name] = isRoot || expandAll;
      if (node.children) {
        node.children.forEach(walk);
      }
    };

    const root = d3.hierarchy(data);
    walk(root);
    setExpandedMap(newMap);
  }, [expandAll, data]);

  return (
    <Box sx={{ overflow: 'scroll' }}>
      <Box ref={containerRef} sx={{ position: 'relative', minWidth: 1110 }}>
        <Stack direction="row" sx={{ mb: 1, position: 'relative', zIndex: 1, px: 1 }}>
          <Box sx={{ flexGrow: 1, pl: 4 }}>
            <Typography variant="caption">Name</Typography>
          </Box>

          {columns.map((col, idx) => (
            <Box
              key={col.key}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              sx={{
                width: col.width,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                position: 'relative',
                userSelect: 'none',
                cursor: 'move',
                padding: '0 .5rem',
              }}
            >
              <TableSortLabel
                active={sortConfig.key === col.key}
                direction={sortConfig.direction}
                onClick={() => handleSort(col.key)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  flexDirection: 'row-reverse',
                  justifyContent: 'space-between',
                  width: '100%',
                  '& svg': {
                    fontSize: '.75rem',
                  },
                }}
              >
                <Typography variant="caption">{col.label}</Typography>
              </TableSortLabel>
            </Box>
          ))}
        </Stack>

        <Box sx={{ position: 'relative' }}>
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {(() => {
              const indexMap = new Map();
              visibleNodes.forEach((n, idx) => {
                indexMap.set(n.data.name, idx);
              });

              return visibleNodes.flatMap((node, i) => {
                if (!node.expanded || !node.children) return [];

                const parentX = node.depth * lineIndent * 8;
                const parentY = (i + 0.5) * rowHeight;

                return node.children
                  .filter((child) => indexMap.has(child.data.name))
                  .map((child) => {
                    const j = indexMap.get(child.data.name);
                    const childX = child.depth * lineIndent * 8;
                    const childY = (j + 0.5) * rowHeight;

                    return (
                      <path
                        key={`line-${node.data.name}-${child.data.name}`}
                        d={`
                                                M${parentX},${parentY}
                                                L${parentX},${childY}
                                                L${childX},${childY}
                                            `}
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth={1}
                      />
                    );
                  });
              });
            })()}
          </svg>

          {visibleNodes.map((node, i) => (
            <Box
              key={node.data.name}
              sx={{
                maxHeight: node.isVisible ? '100px' : '0px',
                opacity: node.isVisible ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-height 250ms ease, opacity 250ms ease',
                pointerEvents: node.isVisible ? 'auto' : 'none',
              }}
            >
              <Box sx={{ position: 'relative', height: rowHeight }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    height: rowHeight,
                    position: 'relative',
                  }}
                >
                  <Box sx={{ flexGrow: 1, pl: node.depth * indent }}>
                    {node.children || node._children ? (
                      <IconButton
                        disableFocusRipple={true}
                        disableRipple={true}
                        size="small"
                        sx={{ marginLeft: '.5rem' }}
                        onClick={() => toggleNode(node)}
                      >
                        {node.expanded ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    ) : null}
                    <Chip
                      size="small"
                      color={
                        node.data.prodType === 'IMO'
                          ? 'primary'
                          : node.data.prodType === 'AGT'
                            ? 'secondary'
                            : 'warning'
                      }
                      variant="outlined"
                      label={node.data.prodType}
                      sx={{ fontSize: '.7rem', margin: '0 .5rem' }}
                    />
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ verticalAlign: 'middle', textTransform: 'capitalize' }}
                    >
                      {node.data.name.replace(
                        /^([^:]+):(.+)$/,
                        (_, before, after) => before.toUpperCase() + ':' + after.toLowerCase()
                      )}
                    </Typography>
                    <Tooltip title="View Profile">
                      <Link
                        to={`/MyProfile/Demographic/${node.data.name.split(':')[0].trim()}`}
                        state={{ prevLocation: location.pathname + location.search }}
                      >
                        <IconButton sx={{ marginLeft: '.5rem' }}>
                          <AccountCircle fontSize="small" />
                        </IconButton>
                      </Link>
                    </Tooltip>
                  </Box>

                  {columns.map((col) => (
                    <Box key={col.key} sx={{ width: col.width, padding: '0 .5rem' }}>
                      <Typography variant="body2" align="right">
                        {col.key.toLowerCase().includes('premium')
                          ? (convertNumberIntoCurrency(node.data[col.key]) ?? '-')
                          : (node.data[col.key] ?? '-')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PendingReportsTree;
